import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, services, projects, testimonials, stats } from '../data/mock';

const Home = () => {
  const featuredProjects = projects.slice(0, 6);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/95 to-neutral-900/80 z-10" />
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
            alt="Modern Architecture"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/40 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-400 text-sm font-medium tracking-wide">
                {companyInfo.experience} Years of Excellence
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              Building
              <span className="block text-red-500">
                Tomorrow's Legacy
              </span>
            </h1>

            <p className="text-lg text-neutral-300 leading-relaxed mb-8 max-w-2xl">
              {companyInfo.overview}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/projects">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-5 group">
                  View Our Work
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-5">
                  Start Your Project
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-neutral-900/90 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-red-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-neutral-400 text-xs tracking-wide">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
                About Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2 mb-4">
                Crafting Excellence
                <span className="block text-red-600">Since 1984</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-4">
                For over four decades, Citiwide Construction Services has been transforming visions into reality across Atlanta and beyond. Our expertise in hospitality, commercial, and residential construction has earned us a reputation for excellence and reliability.
              </p>
              <p className="text-neutral-500 leading-relaxed mb-6 italic text-sm">
                "{companyInfo.mission}"
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                  Learn More About Us
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-transparent rounded-2xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1590007146165-ab670d64a5a2?w=800&q=80"
                alt="Construction Excellence"
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
              What We Do
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-3">
              Our Services
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto text-sm">
              From concept to completion, we provide comprehensive construction services
              tailored to your unique needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="group bg-neutral-800 rounded-xl p-6 border border-neutral-700 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                  <Building2 className="w-6 h-6 text-red-500 group-hover:text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/services">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
                Our Portfolio
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2">
                Featured Projects
              </h2>
            </div>
            <Link to="/projects" className="mt-4 md:mt-0">
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                View All Projects
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredProjects.map((project) => (
              <div
                key={project.id}
                className="group relative overflow-hidden rounded-xl aspect-[4/3] shadow-lg bg-neutral-900 flex items-center justify-center p-6 cursor-pointer hover:-translate-y-1 transition-transform"
              >
                <img
                  src={project.image}
                  alt={project.name}
                  className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                  <span className="text-red-400 text-xs font-medium mb-1 block">
                    {project.category}
                  </span>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {project.name}
                  </h3>
                  <p className="text-neutral-300 text-xs">{project.size}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-red-500 fill-red-500" />
                  ))}
                </div>
                <p className="text-neutral-600 leading-relaxed mb-4 text-sm">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-semibold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="text-neutral-900 font-medium text-sm">{testimonial.name}</div>
                    <div className="text-neutral-500 text-xs">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-red-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to Build Your Vision?
          </h2>
          <p className="text-red-100 max-w-2xl mx-auto mb-6">
            Let's discuss your next project. Our team is ready to bring your construction
            dreams to life with excellence and precision.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white hover:bg-neutral-100 text-red-600 font-semibold px-6 py-5">
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-6 py-5">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
